import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
    X,
    User,
    Users,
    Car,
    Shield,
    Wallet,
    Percent,
    StickyNote,
    ExternalLink,
    Loader2,
    Pencil,
    Banknote,
    FileText,
    CheckCircle2,
    AlertTriangle,
    Clock,
    Phone,
    Mail,
    MapPin,
    ChevronRight,
    Hash,
    FolderOpen,
    Briefcase,
} from "lucide-react";
import { muvekkilKartOzet } from "../lib/api";

function fmtDate(v) {
    if (!v) return "—";
    try {
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return String(v);
        return d.toLocaleDateString("tr-TR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return String(v);
    }
}

function fmtMoney(v) {
    if (v == null || v === "") return "—";
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        maximumFractionDigits: 0,
    }).format(n);
}

function initials(name) {
    return String(name || "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
}

function computePanelLayout(anchor) {
    const margin = 14;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (vw < 768) {
        return {
            left: margin,
            top: margin,
            width: vw - margin * 2,
            height: vh - margin * 2,
        };
    }

    const panelW = Math.min(960, Math.max(720, Math.floor(vw * 0.56)));
    const panelH = Math.min(vh - margin * 2, Math.max(640, Math.floor(vh * 0.88)));

    let left = vw - panelW - margin;
    let top = anchor
        ? Math.max(margin, Math.min(anchor.top - 12, vh - panelH - margin))
        : Math.max(margin, (vh - panelH) / 2);

    if (anchor && anchor.top + 80 > vh - panelH) {
        top = Math.max(margin, vh - panelH - margin);
    }

    return { left, top, width: panelW, height: panelH };
}

function Badge({ children, tone = "gold" }) {
    const tones = {
        gold: "border-[#D4AF37]/40 bg-[#D4AF37]/12 text-[#F3E5AB]",
        violet: "border-violet-500/35 bg-violet-500/12 text-violet-200",
        emerald: "border-emerald-500/35 bg-emerald-500/12 text-emerald-200",
        rose: "border-rose-500/35 bg-rose-500/12 text-rose-200",
        amber: "border-amber-500/35 bg-amber-500/12 text-amber-200",
        slate: "border-white/15 bg-white/5 text-slate-300",
    };
    return (
        <span
            className={`inline-flex items-center rounded-md border px-2.5 py-1 text-[11px] font-medium ${tones[tone] || tones.slate}`}
        >
            {children}
        </span>
    );
}

function KpiTile({ label, value, sub, tone = "default" }) {
    const tones = {
        default: "border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent",
        gold: "border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/10 to-transparent",
        danger: "border-rose-500/30 bg-gradient-to-br from-rose-500/10 to-transparent",
        success: "border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-transparent",
    };
    return (
        <div className={`rounded-xl border p-4 ${tones[tone] || tones.default}`}>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1.5">
                {label}
            </div>
            <div className="text-xl font-semibold font-mono-jb text-slate-100 leading-tight">
                {value}
            </div>
            {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
        </div>
    );
}

function SectionCard({ title, icon: Icon, children, className = "" }) {
    return (
        <section
            className={`rounded-2xl border border-white/10 bg-[#0B0F19]/70 overflow-hidden shadow-lg shadow-black/20 ${className}`}
        >
            <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-white/8 bg-gradient-to-r from-black/30 to-transparent">
                {Icon && <Icon className="h-4 w-4 text-[#D4AF37]" />}
                <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">
                    {title}
                </h3>
            </div>
            <div className="p-5">{children}</div>
        </section>
    );
}

function InfoGrid({ items }) {
    const visible = items.filter((i) => i.value != null && i.value !== "" && i.value !== "—");
    if (!visible.length) return null;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {visible.map((item) => (
                <div key={item.label} className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                        {item.label}
                    </div>
                    <div
                        className={`text-sm text-slate-100 break-words ${item.mono ? "font-mono-jb" : ""}`}
                    >
                        {item.value}
                    </div>
                </div>
            ))}
        </div>
    );
}

function TabButton({ active, icon: Icon, label, count, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all border ${
                active
                    ? "border-[#D4AF37]/40 bg-[#D4AF37]/12 text-[#F3E5AB] shadow-inner shadow-[#D4AF37]/5"
                    : "border-transparent text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
            }`}
        >
            {Icon && <Icon className={`h-4 w-4 shrink-0 ${active ? "text-[#D4AF37]" : ""}`} />}
            <span className="text-sm font-medium flex-1">{label}</span>
            {count != null && count > 0 && (
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-md bg-black/30 text-slate-400">
                    {count}
                </span>
            )}
            {active && <ChevronRight className="h-3.5 w-3.5 opacity-60" />}
        </button>
    );
}

function EmptyState({ text }) {
    return (
        <p className="text-sm text-slate-500 italic py-8 text-center">{text}</p>
    );
}

export default function MuvekkilDetayDrawer({ open, row, anchor, onClose, onEdit }) {
    const [ozet, setOzet] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState("ozet");

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    useEffect(() => {
        if (!open || !row?.id) {
            setOzet(null);
            setTab("ozet");
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        muvekkilKartOzet(row.id)
            .then((data) => {
                if (!cancelled) setOzet(data);
            })
            .catch(() => {
                if (!cancelled) setOzet(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, row?.id]);

    const layout = useMemo(() => computePanelLayout(anchor), [anchor]);

    if (!open || !row) return null;

    const d = ozet || {};
    const oz = d.ozet || {};
    const adSoyad =
        d.ad_soyad ||
        [row.ad, row.soyad].filter(Boolean).join(" ") ||
        row.full_name ||
        "—";

    const tabs = [
        { id: "ozet", label: "Genel Bakış", icon: User },
        {
            id: "dosyalar",
            label: "Sigorta Dosyaları",
            icon: FolderOpen,
            count: oz.dosya_sayisi,
        },
        {
            id: "odeme",
            label: "Ödemeler",
            icon: Wallet,
            count: oz.odeme_kayit_sayisi,
        },
        {
            id: "dk",
            label: "Değer Kaybı",
            icon: Percent,
            count: oz.dk_dosya_sayisi,
        },
        {
            id: "araci",
            label: "Aracılar",
            icon: Users,
            count: (d.aracilar || []).length,
        },
        { id: "notlar", label: "Notlar", icon: StickyNote, count: oz.not_sayisi },
    ];

    return createPortal(
        <>
            <div
                className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-[3px]"
                onClick={onClose}
                aria-hidden
            />
            <aside
                className="fixed z-[301] flex flex-col rounded-2xl border border-[#D4AF37]/25 bg-[#080C14]/98 shadow-2xl shadow-black/70 overflow-hidden animate-in fade-in slide-in-from-right-4 duration-200"
                style={{
                    left: layout.left,
                    top: layout.top,
                    width: layout.width,
                    height: layout.height,
                }}
                role="dialog"
                aria-modal="true"
                aria-label="Müvekkil detayı"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="shrink-0 relative overflow-hidden border-b border-white/10">
                    <div className="absolute inset-0 bg-gradient-to-br from-violet-900/25 via-[#D4AF37]/15 to-sky-900/10" />
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImEiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0ibm9uZSIvPjxwYXRoIGQ9Ik0zMCAwaDEwdjEwSDMweiIgZmlsbD0iI2ZmZiIgZmlsbC1vcGFjaXR5PSIuMDMiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IGZpbGw9InVybCgjYSkiIHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiLz48L3N2Zz4=')] opacity-40" />
                    <div className="relative px-6 py-5">
                        <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0 flex-1 flex gap-4">
                                <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-violet-600/50 to-[#D4AF37]/35 flex items-center justify-center text-xl font-bold text-white shrink-0 shadow-lg">
                                    {initials(adSoyad) || <User className="h-7 w-7" />}
                                </div>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap mb-2">
                                        <Badge tone="gold">
                                            <User className="h-3 w-3 mr-1 inline" />
                                            Müvekkil #{d.id || row.id}
                                        </Badge>
                                        {d.vekalet_durumu_display && (
                                            <Badge
                                                tone={
                                                    d.vekalet_durumu === "ALINDI"
                                                        ? "emerald"
                                                        : "rose"
                                                }
                                            >
                                                Vekalet: {d.vekalet_durumu_display}
                                            </Badge>
                                        )}
                                        {d.anlasma_orani != null && (
                                            <Badge tone="slate">Anlaşma %{d.anlasma_orani}</Badge>
                                        )}
                                        {d.araci_hakedis?.odendi && (
                                            <Badge tone="emerald">Aracı hakediş ödendi</Badge>
                                        )}
                                    </div>
                                    <h2 className="text-2xl font-display font-semibold text-white truncate">
                                        {adSoyad}
                                    </h2>
                                    <p className="text-sm text-slate-400 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                                        {d.tc && (
                                            <span className="font-mono-jb text-xs text-slate-500">
                                                {d.kimlik_tipi_display || "TC"}: {d.tc}
                                            </span>
                                        )}
                                        {d.telefon && (
                                            <span className="inline-flex items-center gap-1">
                                                <Phone className="h-3.5 w-3.5" />
                                                {d.telefon}
                                            </span>
                                        )}
                                        {d.email && (
                                            <span className="inline-flex items-center gap-1 truncate max-w-[200px]">
                                                <Mail className="h-3.5 w-3.5 shrink-0" />
                                                {d.email}
                                            </span>
                                        )}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => onEdit?.(row)}
                                    className="h-10 px-4 rounded-xl border border-white/15 bg-black/20 text-slate-200 hover:bg-white/5 text-sm inline-flex items-center gap-2 transition"
                                >
                                    <Pencil className="h-4 w-4" />
                                    <span className="hidden sm:inline">Düzenle</span>
                                </button>
                                <Link
                                    to={`/e/muvekkiller/${row.id}`}
                                    onClick={onClose}
                                    className="h-10 px-4 rounded-xl border border-sky-500/35 bg-sky-500/15 text-sky-100 hover:bg-sky-500/25 text-sm inline-flex items-center gap-2 transition"
                                >
                                    <ExternalLink className="h-4 w-4" />
                                    <span className="hidden sm:inline">Tam sayfa</span>
                                </Link>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="h-10 w-10 rounded-xl border border-white/15 bg-black/20 text-slate-400 hover:text-white hover:bg-rose-500/15 hover:border-rose-500/30 flex items-center justify-center transition"
                                    aria-label="Kapat"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        {!loading && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-5">
                                <KpiTile
                                    label="Sigorta Dosyası"
                                    value={oz.dosya_sayisi ?? 0}
                                    sub={`${oz.acik_dosya ?? 0} açık`}
                                    tone="gold"
                                />
                                <KpiTile
                                    label="Toplam Ödeme"
                                    value={fmtMoney(oz.toplam_odeme)}
                                    sub={
                                        oz.odenen_odeme != null
                                            ? `Ödenen: ${fmtMoney(oz.odenen_odeme)}`
                                            : null
                                    }
                                    tone="success"
                                />
                                <KpiTile
                                    label="Aracı Ödemeleri"
                                    value={fmtMoney(oz.araci_odeme_toplam)}
                                    sub={
                                        oz.araci_odeme_odenen != null
                                            ? `Ödenen: ${fmtMoney(oz.araci_odeme_odenen)}`
                                            : null
                                    }
                                />
                                <KpiTile
                                    label="Değer Kaybı"
                                    value={fmtMoney(oz.dk_toplam_gercek)}
                                    sub={`${oz.dk_dosya_sayisi ?? 0} dosyada`}
                                />
                                <KpiTile
                                    label="Aracı Hakediş"
                                    value={fmtMoney(d.araci_hakedis?.tutar)}
                                    sub={d.araci_hakedis?.odendi ? "Ödendi" : "Bekliyor"}
                                    tone={d.araci_hakedis?.odendi ? "success" : "default"}
                                />
                                <KpiTile
                                    label="Not / Evrak"
                                    value={oz.not_sayisi ?? 0}
                                    sub={
                                        oz.eksik_evrak_toplam > 0
                                            ? `${oz.eksik_evrak_toplam} eksik evrak`
                                            : `${oz.yatar_sayisi ?? 0} yatar`
                                    }
                                    tone={oz.eksik_evrak_toplam > 0 ? "danger" : "default"}
                                />
                            </div>
                        )}
                    </div>
                </header>

                <div className="flex flex-1 min-h-0">
                    <nav className="hidden md:flex flex-col w-[220px] shrink-0 border-r border-white/8 bg-black/20 p-3 gap-1 overflow-y-auto">
                        {tabs.map((t) => (
                            <TabButton
                                key={t.id}
                                active={tab === t.id}
                                icon={t.icon}
                                label={t.label}
                                count={t.count}
                                onClick={() => setTab(t.id)}
                            />
                        ))}
                    </nav>

                    <div className="md:hidden shrink-0 flex gap-1 overflow-x-auto px-4 py-2 border-b border-white/8 bg-black/20">
                        {tabs.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setTab(t.id)}
                                className={`shrink-0 px-3 py-1.5 rounded-lg text-xs border transition ${
                                    tab === t.id
                                        ? "border-[#D4AF37]/40 bg-[#D4AF37]/12 text-[#F3E5AB]"
                                        : "border-white/10 text-slate-400"
                                }`}
                            >
                                {t.label}
                                {t.count != null && t.count > 0 ? ` (${t.count})` : ""}
                            </button>
                        ))}
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto p-5 md:p-6 space-y-5 bg-gradient-to-b from-transparent to-black/20">
                        {loading && (
                            <div className="flex flex-col items-center justify-center gap-4 py-24 text-slate-400">
                                <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
                                <span className="text-sm">Müvekkil merkezi yükleniyor…</span>
                            </div>
                        )}

                        {!loading && tab === "ozet" && (
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                                <SectionCard title="İletişim & Kimlik" icon={User}>
                                    <InfoGrid
                                        items={[
                                            {
                                                label: "Kimlik türü",
                                                value: d.kimlik_tipi_display,
                                            },
                                            { label: "TC / Vergi No", value: d.tc, mono: true },
                                            { label: "Telefon", value: d.telefon },
                                            { label: "E-posta", value: d.email },
                                            { label: "IBAN", value: d.iban, mono: true },
                                            { label: "Kayıt tarihi", value: fmtDate(d.olusturma_tarihi) },
                                            {
                                                label: "WhatsApp onay",
                                                value: d.whatsapp_onay ? "Evet" : "Hayır",
                                            },
                                        ]}
                                    />
                                    {d.adres && (
                                        <div className="mt-4 flex items-start gap-2 text-sm text-slate-400 border-t border-white/8 pt-4">
                                            <MapPin className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" />
                                            <span className="leading-relaxed">{d.adres}</span>
                                        </div>
                                    )}
                                </SectionCard>

                                <SectionCard title="Finansal Özet" icon={Briefcase}>
                                    <InfoGrid
                                        items={[
                                            {
                                                label: "Toplam ödeme kaydı",
                                                value: fmtMoney(oz.toplam_odeme),
                                            },
                                            {
                                                label: "Ödenen tutar",
                                                value: fmtMoney(oz.odenen_odeme),
                                            },
                                            {
                                                label: "Bekleyen tutar",
                                                value: fmtMoney(oz.bekleyen_odeme),
                                            },
                                            {
                                                label: "Aracı ödemeleri toplam",
                                                value: fmtMoney(oz.araci_odeme_toplam),
                                            },
                                            {
                                                label: "Aracı hakediş",
                                                value: fmtMoney(d.araci_hakedis?.tutar),
                                            },
                                            {
                                                label: "DK toplam (gerçek)",
                                                value: fmtMoney(oz.dk_toplam_gercek),
                                            },
                                        ]}
                                    />
                                </SectionCard>

                                {d.son_not && (
                                    <SectionCard
                                        title="Son Not"
                                        icon={StickyNote}
                                        className="xl:col-span-2"
                                    >
                                        <div className="rounded-xl border border-white/8 bg-black/25 p-4">
                                            <div className="flex items-center gap-2 flex-wrap mb-2">
                                                <Badge tone="slate">{d.son_not.konu}</Badge>
                                                {d.son_not.dosya_no && (
                                                    <Badge tone="violet">{d.son_not.dosya_no}</Badge>
                                                )}
                                                <span className="text-[10px] text-slate-500 ml-auto">
                                                    {fmtDate(d.son_not.tarih)}
                                                </span>
                                            </div>
                                            <p className="text-sm text-slate-300 leading-relaxed">
                                                {d.son_not.ozet}
                                            </p>
                                            {d.son_not.yazan && (
                                                <div className="text-xs text-slate-500 mt-2">
                                                    {d.son_not.yazan}
                                                </div>
                                            )}
                                        </div>
                                    </SectionCard>
                                )}

                                {(d.dosyalar || []).length > 0 && (
                                    <SectionCard
                                        title="Son Dosyalar"
                                        icon={Shield}
                                        className="xl:col-span-2"
                                    >
                                        <div className="space-y-2">
                                            {(d.dosyalar || []).slice(0, 5).map((dosya) => (
                                                <Link
                                                    key={dosya.id}
                                                    to={`/e/sigorta-dosyalari/${dosya.id}`}
                                                    onClick={onClose}
                                                    className="flex items-center gap-3 rounded-xl border border-white/8 bg-black/20 px-4 py-3 hover:border-[#D4AF37]/25 transition group"
                                                >
                                                    <div className="h-9 w-9 rounded-lg bg-violet-500/15 flex items-center justify-center shrink-0">
                                                        <FileText className="h-4 w-4 text-violet-300" />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-mono-jb text-sm text-[#F3E5AB]">
                                                                {dosya.dosya_no}
                                                            </span>
                                                            {dosya.plaka && (
                                                                <span className="text-xs text-slate-500">
                                                                    {dosya.plaka}
                                                                </span>
                                                            )}
                                                            {dosya.dosya_durumu_display && (
                                                                <Badge tone="violet">
                                                                    {dosya.dosya_durumu_display}
                                                                </Badge>
                                                            )}
                                                            {dosya.yatar_acilacak_mi && (
                                                                <Badge tone="amber">Yatar</Badge>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-slate-500 mt-0.5 truncate">
                                                            {dosya.basvuru_konusu_display ||
                                                                dosya.sigorta_sirketi ||
                                                                "—"}
                                                        </div>
                                                    </div>
                                                    <ChevronRight className="h-4 w-4 text-slate-600 group-hover:text-[#D4AF37] shrink-0" />
                                                </Link>
                                            ))}
                                        </div>
                                    </SectionCard>
                                )}
                            </div>
                        )}

                        {!loading && tab === "dosyalar" && (
                            <SectionCard
                                title={`Sigorta Dosyaları (${(d.dosyalar || []).length})`}
                                icon={FolderOpen}
                            >
                                {(d.dosyalar || []).length === 0 ? (
                                    <EmptyState text="Bu müvekkile bağlı sigorta dosyası yok" />
                                ) : (
                                    <div className="space-y-3">
                                        {(d.dosyalar || []).map((dosya) => (
                                            <div
                                                key={dosya.id}
                                                className="rounded-xl border border-white/10 bg-black/25 overflow-hidden hover:border-[#D4AF37]/20 transition"
                                            >
                                                <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 border-b border-white/5 bg-black/20">
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <Link
                                                                to={`/e/sigorta-dosyalari/${dosya.id}`}
                                                                onClick={onClose}
                                                                className="font-mono-jb text-[#F3E5AB] hover:underline"
                                                            >
                                                                {dosya.dosya_no}
                                                            </Link>
                                                            {dosya.plaka && (
                                                                <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                                                                    <Car className="h-3 w-3" />
                                                                    {dosya.plaka}
                                                                </span>
                                                            )}
                                                            {dosya.dosya_durumu_display && (
                                                                <Badge tone="violet">
                                                                    {dosya.dosya_durumu_display}
                                                                </Badge>
                                                            )}
                                                            {dosya.dosya_durumu_display && (
                                                                <Badge tone="slate">
                                                                    {dosya.dosya_durumu_display}
                                                                </Badge>
                                                            )}
                                                            {dosya.yatar_acilacak_mi && (
                                                                <Badge tone="amber">Yatar</Badge>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-slate-500 mt-1">
                                                            {[
                                                                dosya.basvuru_konusu_display,
                                                                dosya.sigorta_sirketi,
                                                                dosya.marka,
                                                                dosya.model,
                                                            ]
                                                                .filter(Boolean)
                                                                .join(" · ")}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap gap-3 text-xs shrink-0">
                                                        <div className="text-right">
                                                            <div className="text-slate-500">Ödeme</div>
                                                            <div className="font-mono-jb text-emerald-300">
                                                                {fmtMoney(
                                                                    dosya.odeme_ozet?.toplam_tutar,
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="text-slate-500">DK</div>
                                                            <div className="font-mono-jb text-[#F3E5AB]">
                                                                {fmtMoney(
                                                                    dosya.deger_kaybi?.gercek,
                                                                )}
                                                            </div>
                                                        </div>
                                                        {dosya.kalan_gun != null && (
                                                            <div className="text-right">
                                                                <div className="text-slate-500">
                                                                    Kalan gün
                                                                </div>
                                                                <div className="font-mono-jb text-slate-200">
                                                                    {dosya.kalan_gun}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                {dosya.son_not && (
                                                    <div className="px-4 py-2.5 text-xs text-slate-400 border-t border-white/5">
                                                        <StickyNote className="h-3 w-3 inline mr-1 text-slate-500" />
                                                        {dosya.son_not.ozet?.slice(0, 120)}
                                                        {dosya.son_not.ozet?.length > 120 ? "…" : ""}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </SectionCard>
                        )}

                        {!loading && tab === "odeme" && (
                            <div className="space-y-5">
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <KpiTile
                                        label="Toplam"
                                        value={fmtMoney(oz.toplam_odeme)}
                                        tone="gold"
                                    />
                                    <KpiTile
                                        label="Ödenen"
                                        value={fmtMoney(oz.odenen_odeme)}
                                        tone="success"
                                    />
                                    <KpiTile
                                        label="Bekleyen"
                                        value={fmtMoney(oz.bekleyen_odeme)}
                                        tone={
                                            Number(oz.bekleyen_odeme) > 0 ? "danger" : "default"
                                        }
                                    />
                                </div>

                                <SectionCard title="Sigorta Ödemeleri" icon={Wallet}>
                                    {(d.odemeler || []).length === 0 ? (
                                        <EmptyState text="Ödeme kaydı bulunamadı" />
                                    ) : (
                                        <div className="overflow-x-auto rounded-xl border border-white/8 -mx-1">
                                            <table className="w-full text-sm min-w-[520px]">
                                                <thead className="bg-black/30 text-[10px] uppercase tracking-wider text-slate-500">
                                                    <tr>
                                                        <th className="text-left px-4 py-2.5">Dosya</th>
                                                        <th className="text-left px-4 py-2.5">Tür</th>
                                                        <th className="text-right px-4 py-2.5">Tutar</th>
                                                        <th className="text-center px-4 py-2.5">Durum</th>
                                                        <th className="text-right px-4 py-2.5">Tarih</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(d.odemeler || []).map((o) => (
                                                        <tr
                                                            key={o.id}
                                                            className="border-t border-white/5 hover:bg-white/[0.02]"
                                                        >
                                                            <td className="px-4 py-3">
                                                                <div className="font-mono-jb text-xs text-[#F3E5AB]">
                                                                    {o.dosya_no}
                                                                </div>
                                                                {o.plaka && (
                                                                    <div className="text-[10px] text-slate-500">
                                                                        {o.plaka}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3 text-slate-300">
                                                                {o.tur_display || o.tur || "—"}
                                                            </td>
                                                            <td className="px-4 py-3 text-right font-mono-jb text-emerald-300">
                                                                {fmtMoney(o.tutar)}
                                                            </td>
                                                            <td className="px-4 py-3 text-center">
                                                                {o.odendi ? (
                                                                    <CheckCircle2 className="h-4 w-4 text-emerald-400 inline" />
                                                                ) : (
                                                                    <Clock className="h-4 w-4 text-amber-400 inline" />
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3 text-right text-xs text-slate-500">
                                                                {fmtDate(o.tarih)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </SectionCard>

                                {(d.araci_odemeleri || []).length > 0 && (
                                    <SectionCard title="Aracı Ödemeleri" icon={Banknote}>
                                        <div className="overflow-x-auto rounded-xl border border-white/8 -mx-1">
                                            <table className="w-full text-sm min-w-[520px]">
                                                <thead className="bg-black/30 text-[10px] uppercase tracking-wider text-slate-500">
                                                    <tr>
                                                        <th className="text-left px-4 py-2.5">Aracı</th>
                                                        <th className="text-left px-4 py-2.5">Dosya</th>
                                                        <th className="text-right px-4 py-2.5">Tutar</th>
                                                        <th className="text-right px-4 py-2.5">Durum</th>
                                                        <th className="text-right px-4 py-2.5">Tarih</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(d.araci_odemeleri || []).map((ao) => (
                                                        <tr
                                                            key={ao.id}
                                                            className="border-t border-white/5"
                                                        >
                                                            <td className="px-4 py-3 text-slate-200">
                                                                {ao.araci_ad}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="font-mono-jb text-xs text-slate-400">
                                                                    {ao.dosya_no}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 text-right font-mono-jb text-[#F3E5AB]">
                                                                {fmtMoney(ao.tutar)}
                                                            </td>
                                                            <td className="px-4 py-3 text-right">
                                                                <Badge
                                                                    tone={
                                                                        ao.durum === "ODENDI"
                                                                            ? "emerald"
                                                                            : ao.durum === "IPTAL"
                                                                              ? "rose"
                                                                              : "amber"
                                                                    }
                                                                >
                                                                    {ao.durum_display || ao.durum}
                                                                </Badge>
                                                            </td>
                                                            <td className="px-4 py-3 text-right text-xs text-slate-500">
                                                                {fmtDate(ao.tarih)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </SectionCard>
                                )}
                            </div>
                        )}

                        {!loading && tab === "dk" && (
                            <SectionCard
                                title={`Değer Kaybı (${(d.deger_kaybi_listesi || []).length})`}
                                icon={Percent}
                            >
                                {(d.deger_kaybi_listesi || []).length === 0 ? (
                                    <EmptyState text="Değer kaybı hesaplanmış dosya yok" />
                                ) : (
                                    <>
                                        <div className="mb-4 p-4 rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5">
                                            <div className="text-[10px] uppercase tracking-wider text-slate-500">
                                                Toplam gerçek değer kaybı
                                            </div>
                                            <div className="text-2xl font-mono-jb text-[#F3E5AB] mt-1">
                                                {fmtMoney(oz.dk_toplam_gercek)}
                                            </div>
                                        </div>
                                        <div className="overflow-x-auto rounded-xl border border-white/8">
                                            <table className="w-full text-sm min-w-[480px]">
                                                <thead className="bg-black/30 text-[10px] uppercase tracking-wider text-slate-500">
                                                    <tr>
                                                        <th className="text-left px-4 py-2.5">Dosya</th>
                                                        <th className="text-left px-4 py-2.5">Plaka</th>
                                                        <th className="text-right px-4 py-2.5">Baz DK</th>
                                                        <th className="text-right px-4 py-2.5">Gerçek DK</th>
                                                        <th className="text-right px-4 py-2.5">Kaza</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(d.deger_kaybi_listesi || []).map((dk) => (
                                                        <tr
                                                            key={dk.dosya_id}
                                                            className="border-t border-white/5 hover:bg-white/[0.02]"
                                                        >
                                                            <td className="px-4 py-3">
                                                                <Link
                                                                    to={`/e/sigorta-dosyalari/${dk.dosya_id}`}
                                                                    onClick={onClose}
                                                                    className="font-mono-jb text-xs text-[#F3E5AB] hover:underline"
                                                                >
                                                                    {dk.dosya_no}
                                                                </Link>
                                                            </td>
                                                            <td className="px-4 py-3 text-slate-400 font-mono-jb text-xs">
                                                                {dk.plaka || "—"}
                                                            </td>
                                                            <td className="px-4 py-3 text-right font-mono-jb text-slate-400">
                                                                {fmtMoney(dk.baz)}
                                                            </td>
                                                            <td className="px-4 py-3 text-right font-mono-jb text-[#F3E5AB]">
                                                                {fmtMoney(dk.gercek)}
                                                            </td>
                                                            <td className="px-4 py-3 text-right text-xs text-slate-500">
                                                                {fmtDate(dk.kaza_tarihi)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </SectionCard>
                        )}

                        {!loading && tab === "araci" && (
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                                <SectionCard
                                    title={`Bağlı Aracılar (${(d.aracilar || []).length})`}
                                    icon={Users}
                                >
                                    {(d.aracilar || []).length === 0 ? (
                                        <EmptyState text="Bu müvekkile bağlı aracı yok" />
                                    ) : (
                                        <div className="space-y-3">
                                            {(d.aracilar || []).map((a) => (
                                                <div
                                                    key={a.id}
                                                    className="rounded-xl border border-white/10 bg-black/25 p-4 hover:border-[#D4AF37]/20 transition"
                                                >
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div>
                                                            <div className="font-semibold text-slate-100">
                                                                {a.ad}
                                                            </div>
                                                            <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                                                                {a.telefon && <div>{a.telefon}</div>}
                                                                {a.email && <div>{a.email}</div>}
                                                                {a.referans_kodu && (
                                                                    <div className="font-mono-jb">
                                                                        Ref: {a.referans_kodu}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="text-[10px] text-slate-500 uppercase">
                                                                Hakediş
                                                            </div>
                                                            <div className="font-mono-jb text-[#F3E5AB]">
                                                                {fmtMoney(a.hak_edis_miktari)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <Link
                                                        to={`/e/aracilar/${a.id}`}
                                                        onClick={onClose}
                                                        className="inline-flex items-center gap-1 text-xs text-sky-300 hover:underline mt-3"
                                                    >
                                                        Aracı kartı <ExternalLink className="h-3 w-3" />
                                                    </Link>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </SectionCard>

                                <SectionCard title="Müvekkil Aracı Hakedişi" icon={Banknote}>
                                    <InfoGrid
                                        items={[
                                            {
                                                label: "Hakediş tutarı",
                                                value: fmtMoney(d.araci_hakedis?.tutar),
                                            },
                                            {
                                                label: "Ödeme durumu",
                                                value: d.araci_hakedis?.odendi
                                                    ? "Ödendi"
                                                    : "Bekliyor",
                                            },
                                            {
                                                label: "Dosya bazlı aracı ödemeleri",
                                                value: fmtMoney(oz.araci_odeme_toplam),
                                            },
                                            {
                                                label: "Ödenen aracı tutarı",
                                                value: fmtMoney(oz.araci_odeme_odenen),
                                            },
                                        ]}
                                    />
                                    {d.araci_hakedis?.tutar != null &&
                                        !d.araci_hakedis?.odendi && (
                                            <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-200">
                                                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                                                Aracı hakedişi henüz ödenmemiş olarak işaretli.
                                            </div>
                                        )}
                                </SectionCard>

                                {(d.araci_odemeleri || []).length > 0 && (
                                    <SectionCard
                                        title="Dosya Bazlı Aracı Ödemeleri"
                                        icon={Wallet}
                                        className="xl:col-span-2"
                                    >
                                        <div className="overflow-x-auto rounded-xl border border-white/8">
                                            <table className="w-full text-sm min-w-[480px]">
                                                <thead className="bg-black/30 text-[10px] uppercase tracking-wider text-slate-500">
                                                    <tr>
                                                        <th className="text-left px-4 py-2.5">Aracı</th>
                                                        <th className="text-left px-4 py-2.5">Dosya</th>
                                                        <th className="text-right px-4 py-2.5">Tutar</th>
                                                        <th className="text-right px-4 py-2.5">Durum</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(d.araci_odemeleri || []).map((ao) => (
                                                        <tr
                                                            key={ao.id}
                                                            className="border-t border-white/5"
                                                        >
                                                            <td className="px-4 py-3 text-slate-200">
                                                                {ao.araci_ad}
                                                            </td>
                                                            <td className="px-4 py-3 font-mono-jb text-xs text-slate-400">
                                                                {ao.dosya_no}
                                                            </td>
                                                            <td className="px-4 py-3 text-right font-mono-jb text-[#F3E5AB]">
                                                                {fmtMoney(ao.tutar)}
                                                            </td>
                                                            <td className="px-4 py-3 text-right">
                                                                <Badge
                                                                    tone={
                                                                        ao.durum === "ODENDI"
                                                                            ? "emerald"
                                                                            : "amber"
                                                                    }
                                                                >
                                                                    {ao.durum_display || ao.durum}
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </SectionCard>
                                )}
                            </div>
                        )}

                        {!loading && tab === "notlar" && (
                            <SectionCard
                                title={`Notlar (${oz.not_sayisi ?? 0})`}
                                icon={StickyNote}
                            >
                                {(d.notlar || []).length === 0 ? (
                                    <EmptyState text="Henüz not kaydı yok" />
                                ) : (
                                    <div className="space-y-3">
                                        {(d.notlar || []).map((note, idx) => (
                                            <article
                                                key={`${note.kaynak}-${note.id}-${idx}`}
                                                className="rounded-xl border border-white/8 bg-black/25 p-4 hover:border-white/15 transition"
                                            >
                                                <div className="flex items-center gap-2 flex-wrap mb-2">
                                                    <Badge tone="slate">{note.konu}</Badge>
                                                    {note.dosya_no && (
                                                        <Badge tone="violet">
                                                            <Hash className="h-3 w-3 mr-0.5 inline" />
                                                            {note.dosya_no}
                                                        </Badge>
                                                    )}
                                                    {note.kaynak && (
                                                        <span className="text-[10px] text-slate-600 uppercase">
                                                            {note.kaynak}
                                                        </span>
                                                    )}
                                                    <span className="text-[10px] text-slate-500 ml-auto">
                                                        {fmtDate(note.tarih)}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
                                                    {note.ozet}
                                                </p>
                                                {(note.yazan || note.kanal) && (
                                                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                                                        {note.yazan && <span>{note.yazan}</span>}
                                                        {note.kanal && (
                                                            <span className="capitalize">
                                                                {note.kanal}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </article>
                                        ))}
                                    </div>
                                )}
                            </SectionCard>
                        )}
                    </div>
                </div>
            </aside>
        </>,
        document.body,
    );
}
